import { useCallback, useEffect, useState } from "react";

import {
  type Chart,
  type ChartData,
  type ChartDataset,
  type ScatterDataPoint,
  type TimeUnit,
} from "chart.js";
import { omit } from "lodash";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { useOrderSummaryGraphLoading } from "@/atoms/order-page-atoms";
import { useUser } from "@components/user-context";
import { debouncedResize } from "@features/sales-v3/utils/chart";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchOrderSummaryGraph,
  type FetchOrderSummaryGraph_me_orderSummaryGraph_data,
  type FetchOrderSummaryGraphVariables,
  MetricFormatters,
} from "@north-beam/nb-client-side";
import { Currencies } from "@north-beam/nb-common";
import { FETCH_ORDER_SUMMARY_GRAPH } from "@pages/settings/queries";

import { BackgroundPlugin } from "./chart-plugins/background-plugin";
import {
  APIGranularity,
  useOrderGraphFilters,
} from "./hooks/use-orders-filters";
import { OrdersMetrics } from "./order-summary-kpis";

function granularityToUnit(granularity: APIGranularity): TimeUnit {
  switch (granularity) {
    case APIGranularity.DAY:
      return "day";
    case APIGranularity.HOUR:
      return "hour";
    case APIGranularity.MONTH:
      return "month";
    case APIGranularity.WEEK:
      return "week";
  }
}

function getCssVariable(name: string, from: HTMLElement = document.body) {
  return getComputedStyle(from).getPropertyValue(name).trim();
}

const buildDataset = (
  graphData?: FetchOrderSummaryGraph_me_orderSummaryGraph_data[],
): ChartData<"line", number[]> => {
  if (!graphData) {
    return { datasets: [] };
  }

  const ordersDataSet: ChartDataset<"line", number[]>["data"] = [];
  const revenueDataSet: ChartDataset<"line", number[]>["data"] = [];

  const dateLabels: ChartData<"line", ScatterDataPoint[], string>["labels"] =
    [];

  graphData.forEach(({ datetime, orderCount, orderRevenue }) => {
    ordersDataSet.push(orderCount);
    revenueDataSet.push(orderRevenue);
    dateLabels.push(datetime);
  });

  const datasets: ChartDataset<"line", number[]>[] = [
    {
      label: "Revenue",
      pointRadius: 0,
      data: revenueDataSet,
      borderColor: "#7081FF",
      backgroundColor: "#7081FF",
      yAxisID: "revenueY",
      borderDash: [4, 2],
      borderWidth: 1,
      tension: 0.4,
    },
    {
      label: "Orders",
      data: ordersDataSet,
      borderColor: "#50BA58",
      backgroundColor: "#50BA58",
      yAxisID: "ordersY",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - it is allowed, but API doesn't show it
      type: "bar",
      maxBarThickness: 80,
      borderRadius: 10,
    },
  ];

  return {
    labels: dateLabels,
    datasets,
  };
};

export function OrdersV2SummaryGraph() {
  const { user } = useUser();
  const { dateRange, granularity, ...restOfFilters } = useOrderGraphFilters();

  const [fontColor, setfontColor] = useState(() =>
    getCssVariable("--v2-text-primary"),
  );

  const { data: graphResponse, loading } = useNorthbeamQuery<
    FetchOrderSummaryGraph,
    FetchOrderSummaryGraphVariables
  >(FETCH_ORDER_SUMMARY_GRAPH, {
    variables: {
      filterOptions: omit(restOfFilters, "compareDateRange"),
      dateRange,
      granularity,
    },
  });
  const [, setOrderQueryLoading] = useOrderSummaryGraphLoading();

  const { datasets, labels } = buildDataset(
    graphResponse?.me.orderSummaryGraph.data,
  );

  const [chartRef, setChartRef] = useState<ChartJSOrUndefined<any>>();

  useEffect(() => {
    setOrderQueryLoading(loading);
  }, [loading, setOrderQueryLoading]);

  useEffect(() => {
    chartRef && debouncedResize(chartRef);
  }, [chartRef, dateRange, restOfFilters]);

  useEffect(() => {
    if (!chartRef) {
      return;
    }

    chartRef.options.plugins!.legend!.labels!.color = fontColor;

    chartRef.update();
  }, [fontColor, chartRef]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newColor = getCssVariable("--v2-text-primary");
      setfontColor(newColor);
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined<any> | null) => {
      if (instance) {
        setChartRef(instance);
      }
    },
    [],
  );

  return (
    <div className="p-4 border border-v2-tertiary rounded-lg h-[560px] text-v2-primary bg-v2-primary flex gap-4 flex-col">
      <div className="flex items-center justify-between ">
        <div className="text-v2-primary font-semibold">Orders Summary</div>
        <OrdersMetrics />
      </div>
      <Line
        ref={setChartInstance}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          onResize(chart: Chart) {
            // on resize, reload the annotations
            debouncedResize(chart);
          },
          plugins: {
            title: {
              display: true,
              align: "start",
              font: {
                size: 10,
              },
            },
            legend: {
              display: true,
              position: "bottom",
              labels: {
                color: fontColor,
                font: {
                  family: "Commons Pro",
                  size: 12,
                },
                usePointStyle: true,
                boxWidth: 8,
                padding: 15,
              },
            },
            tooltip: {
              usePointStyle: true,
              callbacks: {
                label: function (tooltipItem) {
                  if (tooltipItem.dataset.label === "Revenue") {
                    return `${tooltipItem.dataset.label}: ${
                      Currencies.getModel(user.currencies.currentCurrency)
                        .symbol
                    }${tooltipItem.formattedValue}`;
                  }
                  return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
                },
                title: function (tooltipItems) {
                  const datetime = moment.tz(
                    tooltipItems[0].parsed.x,
                    user.timezone,
                  );

                  if (granularity === APIGranularity.DAY) {
                    return datetime.format("dddd, MMMM Do YYYY");
                  } else if (granularity === APIGranularity.MONTH) {
                    return datetime.format("MMMM YYYY");
                  } else if (granularity === APIGranularity.WEEK) {
                    let potentialEndDate = datetime.clone().add(6, "days");
                    const dateRangeEnd = moment(dateRange.end);
                    if (potentialEndDate.isSameOrAfter(dateRangeEnd, "day")) {
                      potentialEndDate = dateRangeEnd;
                    }

                    return `${datetime.format(
                      "ddd, MMM Do YYYY",
                    )} - ${potentialEndDate.format("ddd, MMM Do YYYY")}`;
                  }
                  return datetime.format("dddd, MMMM Do YYYY, h:mm A z");
                },
              },
              labelPointStyle: {
                pointStyle: "circle",
                rotation: 0,
              },
            },
          },
          scales: {
            xAxis: {
              type: "time",
              time: {
                unit: granularityToUnit(granularity),
              },
              ticks: {
                padding: 10,
                maxRotation: 0,
                autoSkip: true,
                color: fontColor,
                font: {
                  family: "Commons Pro",
                  size: 10,
                },
                ...(granularity === APIGranularity.HOUR && {
                  major: {
                    enabled: true,
                  },
                }),
              },
              grid: {
                drawOnChartArea: true,
                drawTicks: false,
                drawBorder: false,
                borderDash: [2, 2],
                color: (context) => {
                  if (context.index === 0) {
                    return "";
                  } else {
                    return getCssVariable("--v2-border-tertiary");
                  }
                },
              },
            },
            revenueY: {
              ticks: {
                callback(tickValue) {
                  return MetricFormatters.currency(
                    Number(tickValue),
                    user.currencies.currentCurrency,
                    {
                      precision: "approximate",
                    },
                  );
                },
                color: fontColor,
                font: {
                  family: "Commons Pro Mono",
                  size: 10,
                },
              },
              beginAtZero: true,
              type: "linear",
              display: true,
              position: "right",

              title: {
                display: true,
                text: "Revenue",
                color: fontColor,
                font: {
                  size: 12,
                  weight: "bold",
                },
              },
              grid: {
                drawOnChartArea: false,
                drawTicks: false,
                drawBorder: false,
              },
            },
            ordersY: {
              type: "linear",
              display: true,
              position: "left",
              title: {
                display: true,
                text: "Orders",
                color: fontColor,
                font: {
                  size: 12,
                  weight: "bold",
                },
              },
              ticks: {
                color: fontColor,
                font: {
                  family: "Commons Pro Mono",
                  size: 10,
                },
              },
              grid: {
                drawOnChartArea: true,
                drawTicks: false,
                drawBorder: false,
                color: (context) => {
                  const ticks = context.scale.ticks;
                  if (
                    context.index === 0 ||
                    context.index === ticks.length - 1
                  ) {
                    return "";
                  } else {
                    return getCssVariable("--v2-border-tertiary");
                  }
                },
              },
            },
          },
          layout: {
            padding: {
              bottom: 36,
            },
          },
        }}
        plugins={[BackgroundPlugin]}
        data={{
          labels,
          datasets,
        }}
      />
    </div>
  );
}
